import { Helmet } from 'react-helmet-async';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  updateStatusOnServer,
  saveNoteOnServer,
  updateNoteOnServer,
  deleteNoteOnServer,
  handleNoteAfterSave,
  handleNoteAfterUpdate,
} from 'src/utils/serverUpdates';
import { Card, Container, Grid, Box } from '@mui/material';
import { UserContext } from '../context/userContext';
import { UserListStatus, UserListToolbar } from '../sections/@dashboard/user';
import useGetLeadsBySearch from 'src/hooks/useGetLeadsBySearch';
import AutoNotification from 'src/components/lead-components/AutoNotification';
import useGetNewLeads from 'src/hooks/useGetNewLeads';
import DriverCardModal from 'src/components/lead-components/DriverCardModal';
import AddNewLeadModal from 'src/components/lead-components/AddNewLeadModal';
import NotePopover from 'src/components/lead-components/NotePopover';
import { AddNewLeadButton } from 'src/components/lead-components/AddNewLeadButton';
import useGetAllRecruiters from 'src/hooks/useGetAllRecruiters';
import { currentDateToTimezone, dayjs } from 'src/helpers/ConvertToTimezone';
import SelectDashboardColumns from 'src/components/lead-components/SelectDashboardColumns';
import DashboardTable from 'src/components/dashboard-table/DashboardTable';
import DeleteNotePopover from 'src/components/lead-components/DeleteNotePopover';
import { displayDashboardColumns } from 'src/utils/dashboardColumnSettings';
import RecruiterSelect from 'src/components/lead-components/RecruiterSelect';
import PositionSelect from 'src/components/lead-components/PositionSelect';
import { getColumnSettingsFromSet, mapColumnData } from 'src/components/dashboard-table/helpers';
import { EditAssignRecruiters } from 'src/components/lead-components/EditAssignRecruiters';
import SnackbarNotification from 'src/components/lead-components/SnackBarNotification';

// function applyFilter(array, query) {
//   if (query) {
//     const lowerCaseQuery = query.toLowerCase();
//     return array.filter((element) => {
//       return (
//         element.lead_first_name.toLowerCase().includes(lowerCaseQuery) ||
//         element.lead_last_name.toLowerCase().includes(lowerCaseQuery) ||
//         // element.lead_full_name.toLowerCase().includes(lowerCaseQuery) ||
//         element.lead_email.toLowerCase().includes(lowerCaseQuery)
//       );
//     });
//   }
//   return array;
// }

export default function UserPage() {
  const { user, fetchingDataLoader, setFetchingDataLoader } = useContext(UserContext);

  const isAdmin = !user?.recruiter_id;

  const [allDrivers, setAllDrivers] = useState([]);
  const [selectedDriverNoteEmail, setSelectedDriverNoteEmail] = useState(null);
  const [currentDriver, setCurrentDriver] = useState({});
  const [currentSearchedStatus, setCurrentSearchedStatus] = useState('all');
  const [totalLeads, setTotalLeads] = useState(0);
  const [displayDataColumns, setDisplayDataColumns] = useState([]);
  const [dataColumnsBooleanState, setDataColumnsBooleanState] = useState({});
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);

  const [editNote, setEditNote] = useState('');
  const [editNoteID, setEditNoteID] = useState('');
  const [isDeletingFlag, setIsDeletingFlag] = useState(false);
  const [isDeletingIndex, setIsDeletingIndex] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingIndex, setIsUpdatingIndex] = useState(null);
  const [deleteSelectedIdx, setDeleteSelectedIdx] = useState(null);

  const [open, setOpen] = useState(null);

  const [page, setPage] = useState(0);

  // const [isUserSidebarOpen, setIsUserSidebarOpen] = useState(false);
  const [anchorDeleteNoteEl, setAnchorDeleteNoteEl] = useState(null);

  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(user.rowsPerPage ?? 50);

  const [allRecruiters, setAllRecruiters] = useState([]);
  const [filterByRecruiter, setFilterByRecruiter] = useState('');
  const [filterByPosition, setFilterByPosition] = useState('all');

  const [newLeads, setNewLeads] = useState([]);
  const [newestLead, setNewestLead] = useState();
  const currentNewestLeadRef = useRef(newestLead);
  const filterByRecruiterRef = useRef(filterByRecruiter);
  const currentSearchedStatusRef = useRef(currentSearchedStatus);
  const pageRef = useRef(page);
  const rowsPerPageRef = useRef(rowsPerPage);
  const currentFilterByPositionRef = useRef(filterByPosition);
  const currentIsStatusUpdatingRef = useRef(isStatusUpdating);
  const currentFilterNameRef = useRef(filterName);
  const [openLeadCard, setOpenLeadCard] = useState(false);
  const [openCreateLead, setOpenCreateLead] = useState(false);
  const isFirstFetch = useRef(true);

  const [openNotification, setOpenNotification] = useState(false);
  const [notificationHandler, setNotificationHandler] = useState({
    title: '',
    severity: '',
  });
  const handleHideNotification = () => {
    setOpenNotification(false);
  };

  const generalFetchLeads = async ({ changedRecruiter, filteredPosition }) => {
    setFetchingDataLoader(false);
    setAllDrivers([]);

    const recruiterId = user.recruiter_id ?? changedRecruiter ?? filterByRecruiter;
    const statusParam = currentSearchedStatus.toLowerCase();

    try {
      const data = await useGetLeadsBySearch({
        companyId: user.company_id,
        leadSource: -1,
        currentStatus: statusParam,
        searchTerm: filterName,
        page: 0,
        recruiterId: recruiterId,
        rowsPerPage,
        filteredPosition: filteredPosition ?? filterByPosition,
      });

      const finalLeads = Array.isArray(data.lead_list)
        ? data.lead_list.map((lead) => {
            return {
              ...lead,
              lead_status: data.status_list[lead.lead_id].status || 'not contacted',
              driverNotes: data.notes_list[lead.lead_id] || [],
              lead_status_modified: data.status_list[lead.lead_id].status_modified,
            };
          })
        : [];

      setTotalLeads(Number(data.total_leads));
      setAllDrivers(finalLeads);
      if (isFirstFetch.current) {
        setNewestLead(data.lead_list[0].lead_id);
        isFirstFetch.current = false;
      }
    } catch (error) {
      console.error('Something went wrong', error);
    } finally {
      setFetchingDataLoader(true);
    }
  };

  const moveColumn = useCallback((fromIndex, toIndex) => {
    setDisplayDataColumns((prevColumns) => {
      const updatedColumns = [...prevColumns];
      const [movedItem] = updatedColumns.splice(fromIndex, 1);
      updatedColumns.splice(toIndex, 0, movedItem);
      localStorage.setItem('dashboardColumnSettings', JSON.stringify(updatedColumns));
      return updatedColumns;
    });
  }, []);
  useEffect(() => {
    const dashboardColumnSettings = JSON.parse(localStorage.getItem('dashboardColumnSettings'));

    setFetchingDataLoader(false);
    if (dashboardColumnSettings) {
      dashboardColumnSettings.filter((column) => isAdmin || column.label !== 'Recruiter');
      setDisplayDataColumns(dashboardColumnSettings);
    } else {
      const columnSettings = getColumnSettingsFromSet(displayDashboardColumns).filter(
        (column) => isAdmin || column.label !== 'Recruiter'
      );
      localStorage.setItem('dashboardColumnSettings', JSON.stringify(columnSettings));
      setDisplayDataColumns(columnSettings);
    }

    const fetchRecruiters = async () => {
      try {
        const recruiters = await useGetAllRecruiters(user.company_id);
        if (Array.isArray(recruiters)) {
          setAllRecruiters(recruiters);
          // setAllRecruiters(recruiters.filter((recruiter) => recruiter.recruiter_status !== 'disabled'));
        } else {
          setAllRecruiters([]);
        }
      } catch (error) {
        console.error('Failed to fetch recruiters', error);
      }
    };
    generalFetchLeads({ changedRecruiter: user.recruiter_id });
    fetchRecruiters();
  }, []);

  useEffect(() => {
    currentSearchedStatusRef.current = currentSearchedStatus;
    pageRef.current = page;
    currentFilterNameRef.current = filterName;
    currentNewestLeadRef.current = newestLead;
    rowsPerPageRef.current = rowsPerPage;
    filterByRecruiterRef.current = filterByRecruiter;
    currentFilterByPositionRef.current = filterByPosition;
    currentIsStatusUpdatingRef.current = isStatusUpdating;
  }, [
    currentSearchedStatus,
    page,
    filterName,
    newestLead,
    rowsPerPage,
    filterByRecruiter,
    filterByPosition,
    isStatusUpdating,
  ]);

  const filterTableHead = (columns, tableHead) => {
    return tableHead.filter((item) => columns[item.label] === true);
  };

  const fetchAndProcessData = async ({ params }) => {
    try {
      const response = await useGetLeadsBySearch(params);

      const finalLeads = Array.isArray(response.lead_list)
        ? response.lead_list.map((lead) => {
            return {
              ...lead,
              lead_status: response.status_list[lead.lead_id].status || 'not contacted',
              driverNotes: response.notes_list[lead.lead_id] || [],
              lead_status_modified: response.status_list[lead.lead_id].status_modified,
            };
          })
        : [];

      setTotalLeads(Number(response.total_leads));
      setAllDrivers(finalLeads);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setFetchingDataLoader(true);
    }
  };

  useEffect(() => {
    const refetchData = async () => {
      const isRefetching = () => {
        return (
          pageRef.current === 0 &&
          (currentSearchedStatusRef.current === 'all' || currentSearchedStatusRef.current === 'new') &&
          currentFilterNameRef.current === ''
        );
      };

      if (isRefetching()) {
        const params = {
          companyId: user.company_id,
          leadSource: -1,
          currentStatus: currentSearchedStatusRef.current,
          searchTerm: currentFilterNameRef.current,
          page: pageRef.current,
          recruiterId: user.recruiter_id ?? filterByRecruiterRef.current,
          rowsPerPage: rowsPerPageRef.current,
          filteredPosition: currentFilterByPositionRef.current,
        };
        fetchAndProcessData({ params });
      }
    };

    const showNotifications = () => {
      useGetNewLeads({
        latestLead: currentNewestLeadRef.current,
        companyId: user.company_id,
        recruiterId: user.recruiter_id ?? '',
      }).then((data) => {
        setNewestLead((prevNewestLead) => data[0]?.lead_id ?? prevNewestLead);
        setNewLeads(data.map((item) => item.lead_full_name.toString()));
      });
    };

    const interval = setInterval(() => {
      if (!currentIsStatusUpdatingRef.current) {
        refetchData();
        showNotifications();
      }
    }, 180000); // 3 minutes

    return () => clearInterval(interval);
  }, []);

  const handleOpenNoteMenu = (event, lead_email) => {
    setOpen(event.currentTarget);
    setSelectedDriverNoteEmail(lead_email);
  };

  const handleOpenNoteUpdateMenu = (event, noteObject, idx) => {
    const { note_id, note_text } = noteObject;
    setOpen(event.currentTarget);
    setEditNote(note_text);
    setEditNoteID(note_id);
    setIsUpdatingIndex(idx);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setSelectedDriverNoteEmail(null);
    setEditNote('');
  };

  const openDeletePopover = (event) => {
    setAnchorDeleteNoteEl(event.currentTarget);
  };

  const closeDeletePopover = () => {
    setAnchorDeleteNoteEl(null);
    setDeleteSelectedIdx(null);
  };

  // const toggleDrawer = (open) => (event) => {
  //   if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
  //     return;
  //   }
  //   setIsUserSidebarOpen(open);
  // };

  const handleOpenLeadCard = (currentId, leadsArray = allDrivers) => {
    const displayedDriver = leadsArray.find((driver) => driver.lead_id === currentId);
    setCurrentDriver(displayedDriver);
    setOpenLeadCard(true);
  };

  const handleOpenCreateLead = () => {
    setOpenCreateLead(true);
  };

  const handleChangePage = (_, newPage) => {
    setFetchingDataLoader(false);
    setAllDrivers([]);
    const statusParam = currentSearchedStatus.toLowerCase();
    useGetLeadsBySearch({
      companyId: user.company_id,
      leadSource: -1,
      currentStatus: statusParam,
      searchTerm: filterName,
      page: newPage,
      recruiterId: user.recruiter_id ?? filterByRecruiter,
      rowsPerPage,
      filteredPosition: filterByPosition,
    })
      .then((data) => {
        const finalLeads = Array.isArray(data.lead_list)
          ? data.lead_list.map((lead) => {
              return {
                ...lead,
                lead_status: data.status_list[lead.lead_id].status || 'not contacted',
                driverNotes: data.notes_list[lead.lead_id] || [],
                lead_status_modified: data.status_list[lead.lead_id].status_modified,
              };
            })
          : [];

        setTotalLeads(Number(data.total_leads));
        setAllDrivers(finalLeads);
      })
      .catch((error) => {
        console.error('Something went wrong', error);
      })
      .finally(() => {
        setPage(newPage);
        setFetchingDataLoader(true);
      });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPage(0);
    setRowsPerPage(newRowsPerPage);
    setFetchingDataLoader(false);

    if (user.rowsPerPage !== newRowsPerPage) {
      user.rowsPerPage = newRowsPerPage;
      localStorage.setItem('user', JSON.stringify(user));
    }

    const params = {
      companyId: user.company_id,
      leadSource: -1,
      currentStatus: currentSearchedStatus,
      searchTerm: filterName,
      page: 0,
      recruiterId: user.recruiter_id ?? filterByRecruiter,
      rowsPerPage: newRowsPerPage,
      driver_position: filterByPosition,
    };
    fetchAndProcessData({ params });
  };

  const handleFilterBySearch = (searchParam) => {
    setPage(0);
    setFilterName(searchParam);
  };

  // const filteredUsers = applyFilter(allDrivers, filterName);

  const isNotFound = allDrivers.length === 0 && !!filterName;

  const handleStatusChange = async (checkLeadId, newStatus) => {
    setIsStatusUpdating(true);
    const updatedDrivers = allDrivers.map((driver) => {
      if (driver.lead_id === checkLeadId) {
        const timestamp = [dayjs(currentDateToTimezone()).format('YYYY-MM-DD HH:mm:ss')];
        const timeLogEntry = {
          [timestamp]: {
            recruiter_id: user.recruiter_id ?? user.company_id,
            event_name: 'status',
            content: newStatus,
          },
        };
        return {
          ...driver,
          lead_status: newStatus,
          lead_status_modified: currentDateToTimezone(),
          lead_event_log: {
            ...driver.lead_event_log,
            ...timeLogEntry,
          },
        };
      }
      return driver;
    });

    if (currentDriver && currentDriver.lead_id === checkLeadId) {
      setCurrentDriver({
        ...currentDriver,
        lead_status: newStatus,
        lead_status_modified: currentDateToTimezone(),
        lead_event_log: {
          ...currentDriver.lead_event_log,
          [dayjs().format('YYYY-MM-DD HH:mm:ss')]: {
            recruiter_id: user.recruiter_id ?? user.company_id,
            event_name: 'status_changed',
            content: `Status changed to: "${newStatus}"`,
          },
        },
      });
    }

    if (newStatus.includes('pass/')) {
      setNotificationHandler({
        title: "You just optimized the ads - we'll not promote your job posts to drivers with similar characteristics",
        severity: 'info',
        color: 'darkred',
        position: { vertical: 'bottom', horizontal: 'left' },
        transition: 'fade',
        width: '400px',
      });
      setOpenNotification(true);
    } else if (newStatus === 'approved') {
      setNotificationHandler({
        title: "You just optimized the ads - we'll promote your job posts to drivers with similar characteristics",
        severity: 'info',
        color: 'lightgreen',
        textColor: 'black',
        position: { vertical: 'bottom', horizontal: 'left' },
        transition: 'fade',
        width: '400px',
      });
      setOpenNotification(true);
    } else if (newStatus === 'hired') {
      setNotificationHandler({
        title: "You just optimized the ads - we'll promote your job posts to drivers with similar characteristics",
        severity: 'info',
        color: 'darkgreen',
        position: { vertical: 'bottom', horizontal: 'left' },
        transition: 'fade',
        width: '400px',
      });
      setOpenNotification(true);
    }

    setAllDrivers(updatedDrivers);

    try {
      const lead = updatedDrivers.find((driver) => driver.lead_id === checkLeadId);
      if (lead) {
        await updateStatusOnServer(lead.lead_id, user.company_id, newStatus, user.recruiter_id);
      }
    } catch (error) {
      console.error('An error occurred:', error);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const handleSaveNote = async (newNoteText) => {
    const findDriver = allDrivers.find((driver) => driver.lead_email === selectedDriverNoteEmail);

    if (findDriver && newNoteText !== '' && newNoteText !== null) {
      const existingNotes = findDriver.driverNotes || [];
      const newNote = {
        note_id: '',
        note_text: newNoteText,
        note_modified_date: currentDateToTimezone(),
      };

      try {
        const data = await saveNoteOnServer(findDriver.lead_id, user.company_id, newNoteText, user.recruiter_id ?? '');
        newNote.note_id = data.toString();
        const timestamp = [dayjs(currentDateToTimezone()).format('YYYY-MM-DD HH:mm:ss')];

        const timeLogEntry = {
          [timestamp]: {
            recruiter_id: user.recruiter_id ?? user.company_id,
            event_name: 'note_created',
            content: newNoteText,
          },
        };

        const updatedDrivers = allDrivers.map((driver) => {
          if (driver.lead_email === selectedDriverNoteEmail) {
            return {
              ...driver,
              driverNotes: [newNote, ...existingNotes],
              lead_event_log: {
                ...driver.lead_event_log,
                ...timeLogEntry,
              },
            };
          }
          return driver;
        });

        setAllDrivers(updatedDrivers);
        const updatedCurrentDriver = updatedDrivers.find((driver) => driver.lead_email === selectedDriverNoteEmail);
        setCurrentDriver(updatedCurrentDriver);
        handleCloseMenu();

        try {
          await handleNoteAfterSave(findDriver.lead_id, user.company_id, newNoteText);
        } catch (error) {
          console.error('An error occurred in handleNoteAfterSave:', error);
        }
      } catch (error) {
        console.error('An error occurred:', error);
        handleCloseMenu();
      }
    }
  };

  //We use the states to alter the selected note data:
  const handleUpdateNote = async (updatedNoteText) => {
    setIsUpdating(true);

    const generateUpdateNoteLog = ({ updatedNoteText, previousNoteText }) => {
      const timestamp = [dayjs(currentDateToTimezone()).format('YYYY-MM-DD HH:mm:ss')];

      return {
        [timestamp]: {
          recruiter_id: user.recruiter_id ?? user.company_id,
          event_name: 'note_updated',
          content: `${previousNoteText} {[(*)]} ${updatedNoteText}`,
        },
      };
    };

    try {
      await updateNoteOnServer(
        currentDriver.lead_id,
        user.company_id,
        editNoteID,
        updatedNoteText,
        user.recruiter_id ?? ''
      );
      setIsUpdating(false);

      const updatedDrivers = allDrivers.map((driver) => {
        const previousNote = driver.driverNotes.find((note) => note.note_id === editNoteID);
        const previousNoteText = previousNote ? previousNote.note_text : '';

        if (driver.lead_email === currentDriver.lead_email) {
          return {
            ...driver,
            driverNotes: [
              { note_id: editNoteID, note_text: updatedNoteText, note_modified_date: currentDateToTimezone() },
              ...currentDriver.driverNotes.filter((note) => note.note_id !== editNoteID),
            ],
            lead_event_log: {
              ...driver.lead_event_log,
              ...generateUpdateNoteLog({ updatedNoteText, previousNoteText }),
            },
          };
        }
        return driver;
      });

      const currentDriverPreviousNote = currentDriver.driverNotes.find((note) => note.note_id === editNoteID);
      const currentDriverPreviousNoteText = currentDriverPreviousNote ? currentDriverPreviousNote.note_text : '';

      setCurrentDriver({
        ...currentDriver,
        driverNotes: [
          { note_id: editNoteID, note_text: updatedNoteText, note_modified_date: currentDateToTimezone() },
          ...currentDriver.driverNotes.filter((note) => note.note_id !== editNoteID),
        ],
        lead_event_log: {
          ...currentDriver.lead_event_log,
          ...generateUpdateNoteLog({ updatedNoteText, previousNoteText: currentDriverPreviousNoteText }),
        },
      });

      setAllDrivers(updatedDrivers);
      handleCloseMenu();

      try {
        await handleNoteAfterUpdate(currentDriver.lead_id, user.company_id, updatedNoteText);
      } catch (error) {
        console.error('An error occurred in handleNoteAfterUpdate:', error);
      }
    } catch (error) {
      console.error('An error occurred while updating the note:', error);
      setIsUpdating(false);
      handleCloseMenu();
    }
  };

  const handleDeleteNote = () => {
    setIsDeletingIndex(deleteSelectedIdx);
    setIsDeletingFlag(true);

    const noteToDelete = currentDriver.driverNotes[deleteSelectedIdx];

    const updatedNotes = currentDriver.driverNotes.filter((_, idx) => idx !== deleteSelectedIdx);

    deleteNoteOnServer(noteToDelete.note_id, currentDriver.lead_id, user.company_id, user.recruiter_id)
      .then(() => {
        const timestamp = [dayjs(currentDateToTimezone()).format('YYYY-MM-DD HH:mm:ss')];
        const timeLogEntry = {
          [timestamp]: {
            recruiter_id: user.recruiter_id ?? user.company_id,
            event_name: 'note_deleted',
            content: noteToDelete.note_text,
          },
        };

        const updatedDrivers = allDrivers.map((driver) => {
          if (driver.lead_email === currentDriver.lead_email) {
            return {
              ...driver,
              driverNotes: updatedNotes,
              lead_event_log: {
                ...driver.lead_event_log,
                ...timeLogEntry,
              },
            };
          }
          return driver;
        });
        setAllDrivers(updatedDrivers);

        setCurrentDriver({
          ...currentDriver,
          driverNotes: updatedNotes,
          lead_event_log: {
            ...currentDriver.lead_event_log,
            ...timeLogEntry,
          },
        });
      })
      .catch((error) => {
        console.error('An error occurred:', error);
      })
      .finally(() => {
        setIsDeletingFlag(false);
        setIsDeletingIndex(null);
      });
  };

  const onStatusToolbarClick = (event) => {
    setCurrentSearchedStatus(event.currentTarget.innerText.toLowerCase());
    currentSearchedStatusRef.current = event.currentTarget.innerText.toLowerCase();
    setPage(0);
  };

  const activeRecruiters = allRecruiters.filter(
    (recruiter) => recruiter.recruiter_status !== 'disabled' && recruiter.recruiter_status !== 'deleted'
  );

  const mappedColumns = useMemo(
    () =>
      mapColumnData(
        displayDataColumns,
        handleOpenLeadCard,
        handleStatusChange,
        handleOpenNoteMenu,
        moveColumn,
        user.company_full_logo
      ),
    [displayDataColumns, handleOpenLeadCard, handleStatusChange, handleOpenNoteMenu, moveColumn, user.company_full_logo]
  );

  const [showEditRecruiter, setShowEditRecruiter] = useState(false);
  const selectedRowsRef = useRef([]);

  const handleSelectRows = (selected) => {
    if (selected.length === 0) {
      setShowEditRecruiter(false);
    }
    if (selected.length > 0 && !showEditRecruiter) {
      setShowEditRecruiter(true);
    }
    selectedRowsRef.current = selected;
  };

  useEffect(() => {
    selectedRowsRef.current = [];
    resetSelection();
    setShowEditRecruiter(false);
  }, [filterByRecruiter]);

  const tableApiRef = useRef(null);

  const resetSelection = () => {
    if (tableApiRef.current?.setRowSelectionModel) {
      tableApiRef.current.setRowSelectionModel([]);
    }
  };
  return (
    <>
      <Helmet>
        <title> {`${user.company_name}`} | Leads </title>
      </Helmet>
      <Container maxWidth="100%" sx={{ height: 'calc(100vh - 72px)', px: '0 !important' }}>
        <Card>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item width={'10%'}>
              <AddNewLeadButton handleOpenCreateLead={handleOpenCreateLead} />
            </Grid>
            <Grid item display="flex" alignItems="center" justifyContent="center" width="10%">
              {showEditRecruiter && !user.recruiter_id && allRecruiters.length > 1 && (
                <EditAssignRecruiters
                  selectedRowsRef={selectedRowsRef}
                  recruiters={activeRecruiters.filter(
                    (recruiter) => recruiter.recruiter_id !== parseInt(filterByRecruiter)
                  )}
                  companyId={user.company_id}
                  setLeads={setAllDrivers}
                  selectedRef={selectedRowsRef.current}
                  resetSelection={resetSelection}
                  setOpenNotification={setOpenNotification}
                  setNotificationHandler={setNotificationHandler}
                />
              )}
            </Grid>
            <Grid item display="flex" alignItems="center" justifyContent="center" width="20%">
              {!user.recruiter_id && allRecruiters.length > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    p: '10px',
                    maxWidth: '100%',
                  }}
                >
                  <RecruiterSelect
                    allRecruiters={allRecruiters}
                    selectedRecruiter={filterByRecruiter}
                    onChange={(changedRecruiter) => {
                      setFilterByRecruiter(changedRecruiter);
                    }}
                    filterBy
                    generalFetchLeads={generalFetchLeads}
                    width="244px"
                  />
                </Box>
              )}
            </Grid>
            <Grid item display="flex" justifyContent="center" width={'20%'}>
              <SelectDashboardColumns
                dataColumnsBooleanState={dataColumnsBooleanState}
                activeColumns={displayDataColumns}
                filterTableHead={filterTableHead}
                setDisplayDataColumns={setDisplayDataColumns}
                setDataColumnsBooleanState={setDataColumnsBooleanState}
                isAdmin={isAdmin}
              />
            </Grid>
            <Grid item display="flex" justifyContent="center" width={'20%'}>
              <Box
                sx={{
                  display: 'flex',
                  p: '10px',
                  maxWidth: '100%',
                }}
              >
                <PositionSelect
                  selectedPosition={filterByPosition}
                  onChange={(selectedPosition) => {
                    setFilterByPosition(selectedPosition);
                  }}
                  filterBy
                  generalFetchLeads={generalFetchLeads}
                  width="244px"
                />
              </Box>
            </Grid>
            <Grid item display="flex" justifyContent="flex-end" width={'20%'}>
              <UserListToolbar
                user={user}
                currentSearchedStatus={currentSearchedStatus}
                setFetchingDataLoader={setFetchingDataLoader}
                setAllDrivers={setAllDrivers}
                filterName={filterName}
                onFilterName={handleFilterBySearch}
                rowsPerPage={rowsPerPage}
                setTotalLeads={setTotalLeads}
                filterByRecruiter={filterByRecruiter}
                filterByPosition={filterByPosition}
              />
            </Grid>
          </Grid>
          <UserListStatus
            user={user}
            onStatusToolbarClick={onStatusToolbarClick}
            setAllDrivers={setAllDrivers}
            setFetchingDataLoader={setFetchingDataLoader}
            rowsPerPage={rowsPerPage}
            setTotalLeads={setTotalLeads}
            filterByRecruiter={filterByRecruiter}
            filterByPosition={filterByPosition}
          />

          <DashboardTable
            setTableApiRef={(api) => (tableApiRef.current = api)}
            allDrivers={allDrivers}
            mappedColumns={mappedColumns}
            setColumns={setDisplayDataColumns}
            fetchingDataLoader={fetchingDataLoader}
            handleOpenLeadCard={handleOpenLeadCard}
            columnsData={displayDataColumns}
            handleStatusChange={handleStatusChange}
            handleOpenNoteMenu={handleOpenNoteMenu}
            isNotFound={isNotFound}
            currentSearchedStatus={currentSearchedStatus}
            companyLogo={user.company_full_logo}
            filterName={filterName}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            totalLeads={Number(totalLeads)}
            page={page}
            activeRecruiters={activeRecruiters}
            isAdmin={isAdmin}
            handleSelectRows={handleSelectRows}
          />
        </Card>
      </Container>
      <NotePopover
        open={open}
        handleCloseMenu={handleCloseMenu}
        editNote={editNote}
        handleSaveNote={handleSaveNote}
        handleUpdateNote={handleUpdateNote}
      />
      {/* Sliding Sidebar TODO: This can be used to display recruiter tasks and reminders */}
      {/* <FullLeadPreview
        isUserSidebarOpen={isUserSidebarOpen}
        toggleDrawer={toggleDrawer}
        currentDriver={currentDriver}
        handleOpenUpdateMenu={handleOpenNoteUpdateMenu}
        isUpdating={isUpdating}
        isUpdatingIndex={isUpdatingIndex}
        isDeletingIndex={isDeletingIndex}
        isDeletingFlag={isDeletingFlag}
        setDeleteSelectedIdx={setDeleteSelectedIdx}
        openDeletePopover={openDeletePopover}
        setCurrentDriver={setCurrentDriver}
        handleStatusChange={handleStatusChange}
      /> */}
      <DriverCardModal
        currentDriver={currentDriver}
        setCurrentDriver={setCurrentDriver}
        allRecruiters={allRecruiters}
        openLeadCard={openLeadCard}
        setOpenLeadCard={setOpenLeadCard}
        handleOpenUpdateMenu={handleOpenNoteUpdateMenu}
        isUpdating={isUpdating}
        isUpdatingIndex={isUpdatingIndex}
        isDeletingIndex={isDeletingIndex}
        isDeletingFlag={isDeletingFlag}
        setDeleteSelectedIdx={setDeleteSelectedIdx}
        openDeletePopover={openDeletePopover}
        handleOpenNoteAddMenu={handleOpenNoteMenu}
        setAllDrivers={setAllDrivers}
        allDrivers={allDrivers}
        user={user}
      />
      <AddNewLeadModal
        openCreateLead={openCreateLead}
        handleOpenLeadCard={handleOpenLeadCard}
        setOpenCreateLead={setOpenCreateLead}
        setAllDrivers={setAllDrivers}
        allDrivers={allDrivers}
        setNewestLead={setNewestLead}
        allRecruiters={allRecruiters}
      />
      <AutoNotification messages={newLeads} />
      <DeleteNotePopover
        anchorDeleteNoteEl={anchorDeleteNoteEl}
        closeDeletePopover={closeDeletePopover}
        handleDeleteNote={handleDeleteNote}
      />
      <SnackbarNotification
        showNotification={openNotification}
        notificationHandler={notificationHandler}
        handleHideNotification={handleHideNotification}
      />
    </>
  );
}
